import type { LucideIcon } from "lucide-react";
import {
	BriefcaseMedical,
	Circle,
	Paperclip,
	TreePalm,
	UserX,
} from "lucide-react";
import { currentWorkDate } from "@/lib/format";
import type {
	AttendanceDay,
	AttendanceDayKind,
	AttendanceDayStatus,
	AttendanceRecordStatus,
	AttendanceTotals,
	AttendanceWorkerDays,
} from "../types";
import { hasOpenEntry, sumMinutes } from "./day";
import { farthestDistance, isFar, punchLocations } from "./location";

export const STATUS_LABELS: Record<AttendanceDayStatus, string> = {
	present: "Presente",
	absent: "Falta",
	medical_leave: "Atestado",
	day_off: "Folga",
	not_scheduled: "Sem jornada",
	pending: "Pendente",
	open: "Em aberto",
};

export const RECORD_STATUSES: AttendanceRecordStatus[] = [
	"present",
	"absent",
	"medical_leave",
	"day_off",
];

export const KIND_LABELS: Record<AttendanceDayKind, string> = {
	weekday: "Dia útil",
	saturday: "Sábado",
	sunday: "Domingo",
	holiday: "Feriado",
};

export function dayKindLabel(
	kind: AttendanceDayKind,
	holidayName?: string,
): string {
	if (kind === "holiday" && holidayName) return `Feriado · ${holidayName}`;
	return KIND_LABELS[kind];
}

export interface CellMeta {
	status: AttendanceDayStatus;
	label: string;
	icon: LucideIcon | null;
	showTotal: boolean;
	pending: boolean;
	hasAttachments: boolean;
}

const STATUS_ICONS: Partial<Record<AttendanceDayStatus, LucideIcon>> = {
	absent: UserX,
	medical_leave: BriefcaseMedical,
	day_off: TreePalm,
	pending: Circle,
};

export const ATTACHMENT_ICON: LucideIcon = Paperclip;

export function cellMeta(day: AttendanceDay): CellMeta {
	const status = day.status;
	return {
		status,
		label: STATUS_LABELS[status],
		icon: STATUS_ICONS[status] ?? null,
		showTotal: day.entries.length > 0,
		pending: status === "pending",
		hasAttachments: (day.record?.attachments.length ?? 0) > 0,
	};
}

export function hasFarPunch(day: AttendanceDay): boolean {
	return day.entries.some((entry) =>
		isFar(farthestDistance(punchLocations(entry))),
	);
}

export function requiresJustification(
	date: string,
	today: string = currentWorkDate(),
): boolean {
	return date < today;
}

export function isFutureDate(
	date: string,
	today: string = currentWorkDate(),
): boolean {
	return date > today;
}

export function dayWorkedMinutes(day: AttendanceDay, now: Date): number {
	if (hasOpenEntry(day.entries)) return sumMinutes(day.entries, now);
	return day.totals.workedMinutes;
}

export function emptyTotals(): AttendanceTotals {
	return {
		workedMinutes: 0,
		expectedMinutes: 0,
		overtimeMinutes: 0,
		missingMinutes: 0,
		overtimePct: 0,
		overtimeEquivalentMinutes: 0,
	};
}

export function sumTotals(days: AttendanceDay[]): AttendanceTotals {
	const totals = emptyTotals();
	for (const day of days) {
		totals.workedMinutes += day.totals.workedMinutes;
		totals.expectedMinutes += day.totals.expectedMinutes;
		totals.overtimeMinutes += day.totals.overtimeMinutes;
		totals.missingMinutes += day.totals.missingMinutes;
		totals.overtimeEquivalentMinutes += day.totals.overtimeEquivalentMinutes;
	}
	return totals;
}

export function countPendingDays(items: AttendanceWorkerDays[]): number {
	let pending = 0;
	for (const item of items) {
		for (const day of item.days) {
			if (day.status === "pending") pending += 1;
		}
	}
	return pending;
}

export function countDaysByStatus(
	days: AttendanceDay[],
	status: AttendanceDayStatus,
): number {
	return days.filter((day) => day.status === status).length;
}

export function findDay(
	items: AttendanceWorkerDays[],
	workerId: string,
	date: string,
): AttendanceDay | undefined {
	return items
		.find((item) => item.worker.id === workerId)
		?.days.find((day) => day.date === date);
}

export function allEntries(items: AttendanceWorkerDays[]) {
	return items.flatMap((item) => item.days.flatMap((day) => day.entries));
}
